import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack, TextField,
} from '@mui/material';
import type { ArraySpec } from '../utils/arrangement';
import { isUsableScale, type Scale } from '../utils/measurement';

interface Props {
  readonly open: boolean;
  /** Units per metre, so the spacing can be asked for in metres where the plan has been measured. */
  readonly scale: Scale;
  readonly onConfirm: (spec: ArraySpec) => void;
  readonly onCancel: () => void;
}

const DEFAULTS = {
  columns: '3', rows: '1', spacingX: '1.8', spacingY: '1.8',
};

/**
 * Copying a selection into a grid.
 *
 * Spacing is centre to centre, and asked for in metres wherever the plan has a scale — a bank
 * of desks is specified as "1.8 m apart", never as "ninety drawing units apart". On a plan
 * nobody has measured it falls back to units and says so, rather than printing a number whose
 * meaning the admin would have to guess.
 */
const ArrayDialog = ({ open, scale, onConfirm, onCancel }: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const [values, setValues] = useState(DEFAULTS);

  const numbers = {
    columns: Math.floor(Number(values.columns)),
    rows: Math.floor(Number(values.rows)),
    spacingX: Number(values.spacingX.replace(',', '.')),
    spacingY: Number(values.spacingY.replace(',', '.')),
  };

  const isValid = Number.isFinite(numbers.columns) && numbers.columns >= 1
    && Number.isFinite(numbers.rows) && numbers.rows >= 1
    && numbers.columns * numbers.rows > 1
    && Number.isFinite(numbers.spacingX)
    && Number.isFinite(numbers.spacingY);

  const confirm = (): void => {
    if (!isValid) {
      return;
    }

    // Converted here rather than in the caller, so the one place that knows the spacing was
    // typed in metres is the one place that turns it into drawing units.
    const factor = isUsableScale(scale) ? scale : 1;

    onConfirm({
      columns: numbers.columns,
      rows: numbers.rows,
      spacingX: numbers.spacingX * factor,
      spacingY: numbers.spacingY * factor,
    });
  };

  const field = (
    key: keyof typeof DEFAULTS,
    labelKey: string,
    step: number,
  ): React.JSX.Element => (
    <TextField
      size="small"
      type="number"
      value={values[key]}
      onChange={(event): void => setValues({ ...values, [key]: event.target.value })}
      label={String(t(labelKey))}
      InputProps={{ inputProps: { step, min: key === 'columns' || key === 'rows' ? 1 : undefined } }}
      fullWidth
    />
  );

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      TransitionProps={{ onExited: (): void => setValues(DEFAULTS) }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{t('floorplan-editor-array-title')}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t(isUsableScale(scale) ? 'floorplan-editor-array-explanation' : 'floorplan-editor-array-explanation-units')}
        </DialogContentText>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            {field('columns', 'floorplan-editor-array-columns', 1)}
            {field('rows', 'floorplan-editor-array-rows', 1)}
          </Stack>
          <Stack direction="row" spacing={2}>
            {field('spacingX', 'floorplan-editor-array-spacing-x', 0.1)}
            {field('spacingY', 'floorplan-editor-array-spacing-y', 0.1)}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{t('cancel')}</Button>
        <Button onClick={confirm} variant="contained" disabled={!isValid}>
          {t('floorplan-editor-array-create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ArrayDialog;
